import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  BillingBalance,
  BillingInvoice,
  BillingPaymentMethod,
  BillingPlan,
  BillingSubscription,
  BillingUsageSummary,
  CreditTransaction,
} from '../../services/billingApi';

interface BillingState {
  loading: boolean;
  error: string | null;
  plans: BillingPlan[];
  subscription: BillingSubscription | null;
  balance: BillingBalance | null;
  usage: BillingUsageSummary | null;
  paymentMethods: BillingPaymentMethod[];
  invoices: BillingInvoice[];
  transactions: CreditTransaction[];
}

const initialState: BillingState = {
  loading: false,
  error: null,
  plans: [],
  subscription: null,
  balance: null,
  usage: null,
  paymentMethods: [],
  invoices: [],
  transactions: [],
};

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    setBillingLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setBillingError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setBillingPlans(state, action: PayloadAction<BillingPlan[]>) {
      state.plans = action.payload;
    },
    setBillingSubscription(state, action: PayloadAction<BillingSubscription | null>) {
      state.subscription = action.payload;
    },
    setBillingBalance(state, action: PayloadAction<BillingBalance | null>) {
      state.balance = action.payload;
    },
    setBillingUsage(state, action: PayloadAction<BillingUsageSummary | null>) {
      state.usage = action.payload;
    },
    setBillingPaymentMethods(state, action: PayloadAction<BillingPaymentMethod[]>) {
      state.paymentMethods = action.payload;
    },
    setBillingInvoices(state, action: PayloadAction<BillingInvoice[]>) {
      state.invoices = action.payload;
    },
    setBillingTransactions(state, action: PayloadAction<CreditTransaction[]>) {
      state.transactions = action.payload;
    },
    clearBillingState(state) {
      state.loading = false;
      state.error = null;
      state.plans = [];
      state.subscription = null;
      state.balance = null;
      state.usage = null;
      state.paymentMethods = [];
      state.invoices = [];
      state.transactions = [];
    },
  },
});

export const {
  setBillingLoading,
  setBillingError,
  setBillingPlans,
  setBillingSubscription,
  setBillingBalance,
  setBillingUsage,
  setBillingPaymentMethods,
  setBillingInvoices,
  setBillingTransactions,
  clearBillingState,
} = billingSlice.actions;

export default billingSlice.reducer;

